import { useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    PiPlusBold, PiPencilSimpleBold, PiTrashBold, PiXBold,
    PiFloppyDiskBold, PiArrowsClockwiseBold, PiWarningBold,
    PiSquaresFourBold, PiListBold, PiMagnifyingGlassBold,
    PiSwatchesBold,
    PiCheckBold,
} from 'react-icons/pi';
import { PageHeader } from '@v2/design-system';
import TablePagination, { TABLE_PER_PAGE_OPTIONS } from '@v2/components/TablePagination';
import api from '@v2/lib/api';
import { cn } from '@v2/utils/cn';

/* ─── Types ──────────────────────────────────────────────────── */
type ProductColor = {
    id: number;
    category: string;
    name: string;
    hex: string | null;
    sort_order: number;
    active: boolean;
};

type ApiResponse = {
    data: ProductColor[];
    meta: { current_page: number; last_page: number; per_page: number; total: number; from: number | null; to: number | null };
};

type CategoryStat = { category: string; label: string; count: number; sample: string[] };

const CATEGORY_LABELS: Record<string, string> = {
    frame:    'Estrutura (Frame)',
    seat:     'Estofado (Seat)',
    stitches: 'Costura (Stitches)',
    arm:      'Braço (Arm)',
};
const CATEGORY_ACCENT: Record<string, string> = {
    frame:    'from-zinc-400 to-zinc-600',
    seat:     'from-amber-400 to-orange-500',
    stitches: 'from-violet-400 to-purple-600',
    arm:      'from-sky-400 to-blue-600',
};
const CATEGORY_ICON_BG: Record<string, string> = {
    frame:    'bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-300',
    seat:     'bg-amber-50 text-amber-600 dark:bg-amber-500/10 dark:text-amber-400',
    stitches: 'bg-violet-50 text-violet-600 dark:bg-violet-500/10 dark:text-violet-400',
    arm:      'bg-sky-50 text-sky-600 dark:bg-sky-500/10 dark:text-sky-400',
};

function catLabel(c: string) { return CATEGORY_LABELS[c] ?? c; }

/* ─── Color swatch ───────────────────────────────────────────── */
function Swatch({ hex, size = 'md' }: { hex: string | null; size?: 'sm' | 'md' | 'lg' }) {
    const sz = size === 'sm' ? 'size-4' : size === 'lg' ? 'size-10' : 'size-6';
    return (
        <div
            className={cn('shrink-0 rounded-full border border-black/10', sz)}
            style={{ background: hex || '#e5e7eb' }}
            title={hex ?? 'sem cor'}
        />
    );
}

/* ─── Form Modal ─────────────────────────────────────────────── */
type FormState = { category: string; name: string; hex: string; sort_order: string; active: boolean };
const EMPTY: FormState = { category: '', name: '', hex: '#000000', sort_order: '0', active: true };

function ColorFormModal({ editing, defaultCategory, onClose }: {
    editing: ProductColor | null;
    defaultCategory?: string;
    onClose: () => void;
}) {
    const backdropRef = useRef<HTMLDivElement>(null);
    const qc = useQueryClient();
    const [form, setForm] = useState<FormState>(EMPTY);
    const [errors, setErrors] = useState<Record<string, string>>({});

    useEffect(() => {
        if (editing) {
            setForm({
                category: editing.category,
                name: editing.name,
                hex: editing.hex || '#000000',
                sort_order: String(editing.sort_order ?? 0),
                active: editing.active,
            });
        } else {
            setForm({ ...EMPTY, category: defaultCategory ?? '' });
        }
    }, [editing, defaultCategory]);

    useEffect(() => {
        const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', h);
        return () => window.removeEventListener('keydown', h);
    }, [onClose]);

    const set = (k: keyof FormState, v: string | boolean) => {
        setForm((f) => ({ ...f, [k]: v }));
        setErrors((e) => { const n = { ...e }; delete n[k]; return n; });
    };

    const { mutate, isPending } = useMutation({
        mutationFn: async () => {
            const payload = { ...form, sort_order: Number(form.sort_order) };
            if (editing) {
                return api.put(`/product-colors/${editing.id}`, payload);
            }
            return api.post('/product-colors', payload);
        },
        onSuccess: () => { qc.invalidateQueries({ queryKey: ['product-colors'] }); onClose(); },
        onError: (err: unknown) => {
            const resp = (err as { response?: { data?: { errors?: Record<string, string[]> } } }).response?.data;
            if (resp?.errors) {
                const flat: Record<string, string> = {};
                for (const [k, v] of Object.entries(resp.errors)) flat[k] = v[0];
                setErrors(flat);
            }
        },
    });

    const inputCls = 'w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 outline-none transition focus:border-amber-400 focus:ring-1 focus:ring-amber-400/30 dark:border-zinc-700 dark:bg-zinc-800/60 dark:text-zinc-200';

    return (
        <div ref={backdropRef} onClick={(e) => { if (e.target === backdropRef.current) onClose(); }}
            className="fixed inset-0 z-50 flex items-start justify-end bg-black/60 backdrop-blur-sm">
            <div className="flex h-full w-full max-w-md flex-col overflow-hidden border-l border-gray-200 bg-white shadow-2xl dark:border-zinc-800 dark:bg-zinc-950">

                <div className="flex shrink-0 items-center justify-between border-b border-gray-100 px-6 py-4 dark:border-zinc-800">
                    <div>
                        <h2 className="text-base font-black text-gray-900 dark:text-white">{editing ? 'Editar cor' : 'Nova cor'}</h2>
                        <p className="text-xs text-gray-400 dark:text-zinc-600">{editing ? editing.name : 'Adicionar ao catálogo'}</p>
                    </div>
                    <button type="button" onClick={onClose} className="flex size-8 items-center justify-center rounded-lg border border-gray-200 text-gray-400 hover:bg-gray-100 dark:border-zinc-700 dark:hover:bg-zinc-800">
                        <PiXBold className="size-4" />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto px-6 py-5">
                    <div className="flex flex-col gap-4">

                        {/* Preview + color picker */}
                        <div className="flex items-center gap-4 rounded-2xl border border-gray-100 bg-gray-50 p-4 dark:border-zinc-800 dark:bg-zinc-900">
                            <div className="size-16 shrink-0 rounded-2xl border border-black/10 shadow-inner" style={{ background: form.hex }} />
                            <div className="flex flex-col gap-2">
                                <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 dark:text-zinc-600">Cor (HEX)</label>
                                <div className="flex items-center gap-2">
                                    <input type="color" value={form.hex} onChange={(e) => set('hex', e.target.value)}
                                        className="size-9 cursor-pointer rounded-lg border border-gray-200 bg-white p-0.5 dark:border-zinc-700 dark:bg-zinc-800" />
                                    <input className={cn(inputCls, 'w-32 font-mono uppercase')} value={form.hex} onChange={(e) => set('hex', e.target.value)} placeholder="#000000" />
                                </div>
                            </div>
                        </div>

                        {/* Nome */}
                        <div className="flex flex-col gap-1">
                            <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 dark:text-zinc-600">Nome *</label>
                            <input className={inputCls} value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="Ex: Matte Black" />
                            {errors.name && <p className="text-xs text-red-500">{errors.name}</p>}
                        </div>

                        {/* Categoria */}
                        <div className="flex flex-col gap-1">
                            <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 dark:text-zinc-600">Categoria *</label>
                            <select className={inputCls} value={form.category} onChange={(e) => set('category', e.target.value)}>
                                <option value="">— Selecione —</option>
                                {Object.entries(CATEGORY_LABELS).map(([k, v]) => (
                                    <option key={k} value={k}>{v}</option>
                                ))}
                            </select>
                            {errors.category && <p className="text-xs text-red-500">{errors.category}</p>}
                        </div>

                        {/* Ordem + Ativo */}
                        <div className="grid grid-cols-2 gap-3">
                            <div className="flex flex-col gap-1">
                                <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 dark:text-zinc-600">Ordem</label>
                                <input className={cn(inputCls, 'font-mono')} type="number" value={form.sort_order} onChange={(e) => set('sort_order', e.target.value)} />
                            </div>
                            <div className="flex flex-col gap-1">
                                <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 dark:text-zinc-600">Status</label>
                                <button type="button" onClick={() => set('active', !form.active)}
                                    className={cn('relative rounded-full transition-colors duration-200', form.active ? 'bg-emerald-500' : 'bg-zinc-300 dark:bg-zinc-700')}
                                    style={{ width: 44, height: 24, marginTop: 6 }}
                                >
                                    <span className="absolute top-[2px] size-5 rounded-full bg-white shadow-md transition-all duration-200" style={{ left: form.active ? 22 : 2 }} />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="flex shrink-0 items-center justify-end gap-3 border-t border-gray-100 px-6 py-4 dark:border-zinc-800">
                    <button type="button" onClick={onClose} className="rounded-xl border border-gray-200 px-4 py-2 text-sm font-bold text-gray-500 hover:bg-gray-50 dark:border-zinc-700 dark:text-zinc-400 dark:hover:bg-zinc-800">
                        Cancelar
                    </button>
                    <button type="button" onClick={() => mutate()} disabled={isPending}
                        className="flex items-center gap-2 rounded-xl bg-amber-500 px-5 py-2 text-sm font-black text-white hover:bg-amber-600 disabled:opacity-60 dark:bg-[#C9A84C] dark:text-zinc-900">
                        {isPending ? <PiArrowsClockwiseBold className="size-4 animate-spin" /> : <PiFloppyDiskBold className="size-4" />}
                        {isPending ? 'Salvando…' : 'Salvar'}
                    </button>
                </div>
            </div>
        </div>
    );
}

/* ─── Delete Confirm ─────────────────────────────────────────── */
function DeleteConfirm({ target, onClose }: { target: ProductColor; onClose: () => void }) {
    const qc = useQueryClient();
    const { mutate, isPending } = useMutation({
        mutationFn: () => api.delete(`/product-colors/${target.id}`),
        onSuccess: () => { qc.invalidateQueries({ queryKey: ['product-colors'] }); onClose(); },
    });
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
            <div className="w-full max-w-sm rounded-2xl border border-gray-200 bg-white p-6 shadow-2xl dark:border-zinc-800 dark:bg-zinc-950">
                <div className="mb-4 flex items-center gap-3">
                    <div className="flex size-10 items-center justify-center rounded-xl bg-red-50 dark:bg-red-500/10">
                        <PiWarningBold className="size-5 text-red-500" />
                    </div>
                    <div>
                        <h3 className="font-black text-gray-900 dark:text-white">Excluir cor</h3>
                        <p className="text-xs text-gray-400 dark:text-zinc-600">{target.name} · {catLabel(target.category)}</p>
                    </div>
                    <div className="ml-auto size-8 shrink-0 rounded-full border border-black/10" style={{ background: target.hex || '#e5e7eb' }} />
                </div>
                <p className="mb-5 text-sm text-gray-500 dark:text-zinc-400">Esta ação não pode ser desfeita.</p>
                <div className="flex justify-end gap-3">
                    <button type="button" onClick={onClose} className="rounded-xl border border-gray-200 px-4 py-2 text-sm font-bold text-gray-500 hover:bg-gray-50 dark:border-zinc-700 dark:text-zinc-400 dark:hover:bg-zinc-800">Cancelar</button>
                    <button type="button" onClick={() => mutate()} disabled={isPending}
                        className="flex items-center gap-2 rounded-xl bg-red-500 px-4 py-2 text-sm font-black text-white hover:bg-red-600 disabled:opacity-60">
                        {isPending ? <PiArrowsClockwiseBold className="size-4 animate-spin" /> : <PiTrashBold className="size-4" />}
                        {isPending ? 'Excluindo…' : 'Excluir'}
                    </button>
                </div>
            </div>
        </div>
    );
}

/* ─── Main Page ──────────────────────────────────────────────── */
export default function ProductColorsPage() {
    const [activeCategory, setActiveCategory] = useState<string>('all');
    const [viewMode, setViewMode] = useState<'card' | 'list'>('card');
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(25);
    const [formOpen, setFormOpen] = useState(false);
    const [editing, setEditing] = useState<ProductColor | null>(null);
    const [deleting, setDeleting] = useState<ProductColor | null>(null);

    useEffect(() => { setPage(1); }, [search, activeCategory, perPage]);

    // Busca todas as cores para montar os stats por categoria
    const { data: allData } = useQuery({
        queryKey: ['product-colors', 'all-stats'],
        queryFn: async () => {
            const { data: body } = await api.get<ApiResponse>('/product-colors', { params: { per_page: '200' } });
            return body.data;
        },
        staleTime: 60_000,
    });

    // Busca paginada com filtros
    const { data, isLoading } = useQuery({
        queryKey: ['product-colors', activeCategory, search, page, perPage],
        queryFn: async () => {
            const params: Record<string, string> = { page: String(page), per_page: String(perPage) };
            if (search) params.search = search;
            if (activeCategory !== 'all') params.category = activeCategory;
            const { data: body } = await api.get<ApiResponse>('/product-colors', { params });
            return body;
        },
        placeholderData: (prev) => prev,
        staleTime: 30_000,
    });

    // Stats por categoria a partir de allData
    const categoryStats: CategoryStat[] = (() => {
        if (!allData) return [];
        const grouped = allData.reduce<Record<string, ProductColor[]>>((acc, c) => {
            if (!acc[c.category]) acc[c.category] = [];
            acc[c.category].push(c);
            return acc;
        }, {});
        return Object.entries(grouped).map(([cat, items]) => ({
            category: cat,
            label: catLabel(cat),
            count: items.length,
            sample: items.filter((c) => c.hex).slice(0, 5).map((c) => c.hex!),
        }));
    })();

    const rows = data?.data ?? [];
    const meta = data?.meta;

    return (
        <div className="space-y-6">
            <PageHeader
                title="Cores de Produto"
                breadcrumb={[{ name: 'Configurações' }, { name: 'Cores de Produto' }]}
            >
                <button
                    type="button"
                    onClick={() => { setEditing(null); setFormOpen(true); }}
                    className="flex items-center gap-2 rounded-xl bg-amber-500 px-4 py-2 text-sm font-black text-white transition hover:bg-amber-600 dark:bg-[#C9A84C] dark:text-zinc-900"
                >
                    <PiPlusBold className="size-4" />
                    Nova cor
                </button>
            </PageHeader>

            {/* ── KPI Cards por categoria ── */}
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
                {/* Card "Todas" */}
                <button
                    type="button"
                    onClick={() => setActiveCategory('all')}
                    className={cn(
                        'flex flex-col gap-3 rounded-2xl border p-4 text-left transition hover:shadow-sm',
                        activeCategory === 'all'
                            ? 'border-amber-400 bg-amber-50 dark:border-amber-500/50 dark:bg-amber-500/10'
                            : 'border-gray-200 bg-white dark:border-zinc-800 dark:bg-zinc-900',
                    )}
                >
                    <div className={cn('flex size-10 items-center justify-center rounded-xl', activeCategory === 'all' ? 'bg-amber-100 text-amber-600 dark:bg-amber-500/20 dark:text-amber-400' : 'bg-gray-100 text-gray-500 dark:bg-zinc-800 dark:text-zinc-400')}>
                        <PiSwatchesBold className="size-5" />
                    </div>
                    <div>
                        <p className="text-xl font-black text-gray-900 dark:text-white">{allData?.length ?? 0}</p>
                        <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 dark:text-zinc-600">Todas</p>
                    </div>
                    {activeCategory === 'all' && <PiCheckBold className="absolute right-3 top-3 size-4 text-amber-500" />}
                </button>

                {categoryStats.map((cs) => (
                    <button
                        key={cs.category}
                        type="button"
                        onClick={() => setActiveCategory(cs.category)}
                        className={cn(
                            'relative flex flex-col gap-3 rounded-2xl border p-4 text-left transition hover:shadow-sm',
                            activeCategory === cs.category
                                ? 'border-amber-400 bg-amber-50 dark:border-amber-500/50 dark:bg-amber-500/10'
                                : 'border-gray-200 bg-white dark:border-zinc-800 dark:bg-zinc-900',
                        )}
                    >
                        {/* Faixa colorida topo */}
                        <div className={cn('h-1 w-full rounded-full bg-gradient-to-r', CATEGORY_ACCENT[cs.category] ?? 'from-gray-300 to-gray-400')} />

                        <div className={cn('flex size-10 items-center justify-center rounded-xl text-sm font-black', CATEGORY_ICON_BG[cs.category] ?? 'bg-gray-100 text-gray-500')}>
                            {cs.count}
                        </div>

                        {/* Swatches */}
                        <div className="flex items-center gap-1">
                            {cs.sample.map((hex, i) => (
                                <div key={i} className="size-4 rounded-full border border-black/10 shadow-sm" style={{ background: hex }} />
                            ))}
                            {cs.sample.length === 0 && (
                                <span className="text-[10px] text-gray-400">sem hex</span>
                            )}
                        </div>

                        <div>
                            <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 dark:text-zinc-600">{cs.label}</p>
                        </div>

                        {activeCategory === cs.category && (
                            <PiCheckBold className="absolute right-3 top-3 size-4 text-amber-500" />
                        )}
                    </button>
                ))}
            </div>

            {/* ── Toolbar ── */}
            <div className="flex flex-wrap items-center gap-3">
                <div className="flex flex-1 items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2.5 dark:border-zinc-800 dark:bg-zinc-900">
                    <PiMagnifyingGlassBold className="size-4 shrink-0 text-gray-400" />
                    <input
                        className="flex-1 bg-transparent text-sm text-gray-800 outline-none placeholder:text-gray-400 dark:text-zinc-200"
                        placeholder="Buscar cor…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                    {search && <button type="button" onClick={() => setSearch('')} className="text-gray-400 hover:text-gray-600"><PiXBold className="size-3.5" /></button>}
                </div>

                {/* Toggle visão */}
                <div className="flex overflow-hidden rounded-xl border border-gray-200 dark:border-zinc-700">
                    {(['card', 'list'] as const).map((mode) => (
                        <button key={mode} type="button" onClick={() => setViewMode(mode)}
                            title={mode === 'card' ? 'Cards' : 'Lista'}
                            className={cn('flex size-9 items-center justify-center transition',
                                viewMode === mode
                                    ? 'bg-amber-500 text-white dark:bg-[#C9A84C] dark:text-zinc-900'
                                    : 'bg-white text-gray-400 hover:bg-gray-50 dark:bg-zinc-900 dark:text-zinc-500 dark:hover:bg-zinc-800')}>
                            {mode === 'card' ? <PiSquaresFourBold className="size-4" /> : <PiListBold className="size-4" />}
                        </button>
                    ))}
                </div>
            </div>

            {/* ── Conteúdo ── */}
            {isLoading ? (
                <div className={viewMode === 'card' ? 'grid gap-3 sm:grid-cols-3 lg:grid-cols-5' : 'space-y-2'}>
                    {Array.from({ length: perPage }).map((_, i) => (
                        <div key={i} className="h-16 animate-pulse rounded-xl bg-gray-100 dark:bg-zinc-800" />
                    ))}
                </div>
            ) : rows.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-gray-400 dark:text-zinc-600">
                    <PiSwatchesBold className="mb-2 size-10 opacity-30" />
                    <p className="font-bold">Nenhuma cor encontrada</p>
                </div>
            ) : viewMode === 'card' ? (

                /* ── Card view ── */
                <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-5">
                    {rows.map((c) => (
                        <div key={c.id} className="group relative flex flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white dark:border-zinc-800 dark:bg-zinc-900">
                            {/* Swatch grande */}
                            <div className="h-16 w-full" style={{ background: c.hex || '#e5e7eb' }} />

                            <div className="flex flex-1 flex-col gap-1 p-3">
                                <p className="truncate text-xs font-black text-gray-800 dark:text-zinc-100">{c.name}</p>
                                <div className="flex items-center justify-between gap-1">
                                    <span className="rounded-full border border-gray-100 bg-gray-50 px-1.5 py-0.5 text-[10px] font-bold text-gray-400 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-600">
                                        {catLabel(c.category)}
                                    </span>
                                    {c.hex && (
                                        <span className="font-mono text-[10px] text-gray-400 dark:text-zinc-600">{c.hex}</span>
                                    )}
                                </div>
                                {!c.active && (
                                    <span className="rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-400 dark:bg-zinc-800 dark:text-zinc-600">Inativa</span>
                                )}
                            </div>

                            {/* Actions overlay */}
                            <div className="flex border-t border-gray-100 dark:border-zinc-800">
                                <button type="button" onClick={() => { setEditing(c); setFormOpen(true); }}
                                    className="flex flex-1 items-center justify-center gap-1 py-2 text-[11px] font-bold text-gray-400 transition hover:bg-amber-50 hover:text-amber-600 dark:text-zinc-600 dark:hover:bg-amber-500/10 dark:hover:text-amber-400">
                                    <PiPencilSimpleBold className="size-3" />Editar
                                </button>
                                <div className="w-px bg-gray-100 dark:bg-zinc-800" />
                                <button type="button" onClick={() => setDeleting(c)}
                                    className="flex flex-1 items-center justify-center gap-1 py-2 text-[11px] font-bold text-gray-400 transition hover:bg-red-50 hover:text-red-500 dark:text-zinc-600 dark:hover:bg-red-500/10 dark:hover:text-red-400">
                                    <PiTrashBold className="size-3" />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (

                /* ── List view ── */
                <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white dark:border-zinc-800 dark:bg-zinc-900">
                    <table className="min-w-full text-sm">
                        <thead className="border-b border-gray-100 dark:border-zinc-800">
                            <tr className="text-[10px] font-black uppercase tracking-wider text-gray-400 dark:text-zinc-600">
                                <th className="px-4 py-3 text-left" style={{ width: '3rem' }}>Cor</th>
                                <th className="px-4 py-3 text-left">Nome</th>
                                <th className="px-4 py-3 text-left">Categoria</th>
                                <th className="px-4 py-3 text-left">HEX</th>
                                <th className="px-4 py-3 text-center">Ordem</th>
                                <th className="px-4 py-3 text-center">Status</th>
                                <th className="px-4 py-3"></th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-zinc-800/60">
                            {rows.map((c) => (
                                <tr key={c.id} className="transition hover:bg-gray-50/60 dark:hover:bg-zinc-800/40">
                                    <td className="px-4 py-3">
                                        <Swatch hex={c.hex} size="md" />
                                    </td>
                                    <td className="px-4 py-3 font-semibold text-gray-800 dark:text-zinc-100">{c.name}</td>
                                    <td className="px-4 py-3">
                                        <span className={cn('rounded-full px-2.5 py-0.5 text-[11px] font-bold', CATEGORY_ICON_BG[c.category] ?? 'bg-gray-100 text-gray-500')}>
                                            {catLabel(c.category)}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 font-mono text-xs text-gray-500 dark:text-zinc-400">{c.hex ?? '—'}</td>
                                    <td className="px-4 py-3 text-center text-xs text-gray-400 dark:text-zinc-600">{c.sort_order}</td>
                                    <td className="px-4 py-3 text-center">
                                        {c.active
                                            ? <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-black text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-400">Ativa</span>
                                            : <span className="rounded-full border border-gray-200 bg-gray-100 px-2 py-0.5 text-[11px] font-black text-gray-400 dark:border-zinc-700 dark:bg-zinc-800">Inativa</span>
                                        }
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                        <div className="flex items-center justify-end gap-1">
                                            <button type="button" onClick={() => { setEditing(c); setFormOpen(true); }}
                                                className="flex size-8 items-center justify-center rounded-lg border border-gray-200 text-gray-400 hover:border-amber-300 hover:bg-amber-50 hover:text-amber-600 dark:border-zinc-700 dark:hover:border-amber-500/30 dark:hover:bg-amber-500/10 dark:hover:text-amber-400">
                                                <PiPencilSimpleBold className="size-3.5" />
                                            </button>
                                            <button type="button" onClick={() => setDeleting(c)}
                                                className="flex size-8 items-center justify-center rounded-lg border border-gray-200 text-gray-400 hover:border-red-300 hover:bg-red-50 hover:text-red-500 dark:border-zinc-700 dark:hover:border-red-500/30 dark:hover:bg-red-500/10 dark:hover:text-red-400">
                                                <PiTrashBold className="size-3.5" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {meta && (
                <TablePagination
                    meta={meta}
                    onPageChange={setPage}
                    onPerPageChange={(value) => {
                        setPerPage(value);
                        setPage(1);
                    }}
                    perPageOptions={[...TABLE_PER_PAGE_OPTIONS]}
                    itemLabel="cor"
                />
            )}

            {formOpen && (
                <ColorFormModal
                    editing={editing}
                    defaultCategory={activeCategory !== 'all' ? activeCategory : undefined}
                    onClose={() => { setFormOpen(false); setEditing(null); }}
                />
            )}
            {deleting && <DeleteConfirm target={deleting} onClose={() => setDeleting(null)} />}
        </div>
    );
}
